import { useState } from "react";
import { Loader2, X } from "lucide-react";
import { deveGuardarNoCatalogo, resumoDaFace, type MarcacaoFacial } from "@bookdental/schemas";
import { Button, Checkbox, Input, Label } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { usePermissao } from "@/lib/use-permissao";
import { parsePriceInput } from "@/components/services/service-meta";
import { CampoDeProcedimento } from "./CampoDeProcedimento";
import type { Procedimento } from "./SeletorDeProcedimento";
import { useCatalogoDoItem } from "./use-catalogo-do-item";
import { paraCampo, reais, usePlanoMutations } from "./use-plano";

export type ServicoDoItem = { id: string; priceCents: number; color: string | null };
export type ItemEmEdicao = { id: string; nome: string; precoCents: number | null; porDente: boolean; offeredService: ServicoDoItem | null };

/**
 * "Adicionar procedimento", no meio do editor: os dentes vêm da arcada ao lado (ou, no modo Face, as
 * marcações do rosto); o procedimento vem
 * dos botões (favoritos e os do dentista) ou da busca; o preço sugere o do catálogo. Procedimento
 * cobrado por dente multiplica pelo número de dentes marcados.
 */
export function FormularioDoItem({
  garantirPlano,
  item,
  dentistaId,
  dentes,
  quantidadeDeDentes,
  dentesTravados,
  modoFace,
  marcacoes,
  unidade,
  onDentesManuais,
  onLimparDentes,
  onLimparMarcacoes,
  onProcedimento,
  onConcluido,
}: {
  /** Id do plano — cria o plano na hora, se for o primeiro procedimento de um plano novo. */
  garantirPlano: () => Promise<string>;
  item: ItemEmEdicao | null;
  dentistaId: string | null;
  dentes: string;
  quantidadeDeDentes: number;
  dentesTravados: boolean;
  modoFace: boolean;
  marcacoes: MarcacaoFacial[];
  unidade: string | null;
  onDentesManuais: (v: string) => void;
  onLimparDentes: () => void;
  onLimparMarcacoes: () => void;
  /** Procedimento escolhido (ou nenhum, quando o nome é digitado à mão). */
  onProcedimento: (p: Procedimento | null) => void;
  onConcluido: () => void;
}) {
  const m = usePlanoMutations();
  const podeEditarPrecos = usePermissao("prices.manage");
  const dentistas = trpc.organization.listDentists.useQuery({ apenasAtivos: true });
  const atribuidos = dentistas.data?.find((d) => d.id === dentistaId)?.assignedServiceIds ?? [];
  const [nome, setNome] = useState(item?.nome ?? "");
  const [servico, setServico] = useState<ServicoDoItem | null>(item?.offeredService ?? null);
  const [preco, setPreco] = useState(paraCampo(item?.precoCents));
  const [porDente, setPorDente] = useState(item?.porDente ?? false);
  const [guardar, setGuardar] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const precoCents = preco.trim() ? parsePriceInput(preco) : null;
  // Na face não existe cobrança por dente: o preço é do procedimento.
  const cobraPorDente = porDente && !modoFace;
  const quantidade = cobraPorDente ? Math.max(quantidadeDeDentes, 1) : 1;
  const vaiGuardarSozinho = !!servico && deveGuardarNoCatalogo(servico.priceCents, precoCents);
  const mudouPreco = !!servico && servico.priceCents > 0 && precoCents != null && precoCents !== servico.priceCents;

  const catalogo = useCatalogoDoItem({
    modoFace,
    onEscolher: (p) => escolher(p),
    // Desfeito o "adicionar ao catálogo", o item não pode ficar preso ao procedimento apagado.
    onDesfeito: (id) => {
      if (servico?.id !== id) return;
      setServico(null);
      setNome("");
      onProcedimento(null);
    },
  });
  const escolher = (p: Procedimento) => {
    setNome(p.name);
    setServico(p.id ? { id: p.id, priceCents: p.priceCents, color: p.color } : null);
    setPorDente(p.cobradoPorDente);
    onProcedimento(p);
    if (p.priceCents) setPreco(paraCampo(p.priceCents));
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const planoId = await garantirPlano();
      await m.salvarItem.mutateAsync({
        planoId,
        itemId: item?.id,
        offeredServiceId: servico?.id ?? null,
        nome: nome.trim(),
        dentes: dentes.trim() || null,
        precoCents,
        porDente: cobraPorDente,
        quantidade,
        marcacoes,
      });
      if (guardar && mudouPreco && servico && precoCents) m.guardarPreco.mutate({ serviceId: servico.id, precoCents });
      onConcluido();
    } catch {
      // o toast de erro vem das mutations
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="space-y-4 rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-border dark:bg-card">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{item ? "Editar procedimento" : "Adicionar procedimento"}</p>
        {item && (
          <button type="button" onClick={onConcluido} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
            <X className="h-3.5 w-3.5" /> Cancelar edição
          </button>
        )}
      </div>
      {modoFace ? (
        <div className="space-y-1.5">
          <Label>Região na face</Label>
          <div className="flex gap-2">
            <p className="min-h-10 flex-1 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700 dark:border-border dark:bg-muted/40 dark:text-foreground">
              {resumoDaFace(marcacoes, unidade) || <span className="text-zinc-400">Marque no rosto ao lado</span>}
            </p>
            {marcacoes.length > 0 && (
              <Button type="button" variant="outline" size="sm" className="h-10" onClick={onLimparMarcacoes}>Limpar</Button>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="item-dentes">Dentes / região</Label>
          <div className="flex gap-2">
            <Input
              id="item-dentes"
              value={dentes}
              disabled={dentesTravados}
              placeholder="Marque na arcada ou digite (ex.: 11, 21)"
              onChange={(e) => onDentesManuais(e.target.value)}
            />
            {dentes && (
              <Button type="button" variant="outline" size="sm" className="h-10" onClick={onLimparDentes}>Limpar</Button>
            )}
          </div>
        </div>
      )}
      <CampoDeProcedimento
        nome={nome}
        servicoId={servico?.id ?? null}
        modoFace={modoFace}
        atribuidos={atribuidos}
        catalogo={catalogo}
        onEscolher={escolher}
        onMudarNome={(v) => {
          setNome(v);
          setServico(null);
          onProcedimento(null);
        }}
      />
      <div className="space-y-1.5">
        <Label htmlFor="item-preco">{cobraPorDente ? "Preço por dente (R$)" : "Preço ao paciente (R$)"}</Label>
        <Input id="item-preco" inputMode="decimal" value={preco} placeholder="Opcional" onChange={(e) => setPreco(e.target.value)} />
        {!modoFace && (
          <label className="flex items-center gap-2 text-[12px] text-zinc-700 dark:text-foreground">
            <Checkbox checked={porDente} onCheckedChange={(v) => setPorDente(v === true)} />
            Cobrar por dente
          </label>
        )}
        {cobraPorDente && precoCents != null && (
          <p className="text-[12px] text-zinc-600 dark:text-muted-foreground">
            {quantidade} dente{quantidade > 1 ? "s" : ""} × {reais(precoCents)} = <b>{reais(precoCents * quantidade)}</b>
          </p>
        )}
        {vaiGuardarSozinho && (
          <p className="text-[11.5px] text-emerald-700">Este procedimento ainda não tem preço: o valor vai ficar guardado nele.</p>
        )}
        {mudouPreco &&
          (podeEditarPrecos ? (
            <label className="flex items-center gap-2 text-[12px] text-zinc-700 dark:text-foreground">
              <Checkbox checked={guardar} onCheckedChange={(v) => setGuardar(v === true)} />
              Guardar para os próximos planos (hoje {reais(servico!.priceCents)}, pede o PIN)
            </label>
          ) : (
            <p className="text-[11.5px] text-zinc-500">Vale só para este plano — o preço do catálogo é {reais(servico!.priceCents)}.</p>
          ))}
      </div>
      <Button className="w-full" disabled={nome.trim().length < 2 || salvando} onClick={salvar}>
        {salvando && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
        {item ? "Salvar procedimento" : "Adicionar ao plano"}
      </Button>
    </div>
  );
}
